<template>
  <div>
    <div class="container-transition">
    <template v-if="!exportSection">

      <transition name="counter-transition">
        {{ transactionDashboard  }}
        <div
          v-if="transactionDashboard && !isInBudgetSection && !$route.meta.users && selectedTransactions.length === 0"
          class="Fuell-Table--counter"
        >
          <i18n path="transactions_table_total_count_and_amount">
            <template v-slot:totalItems>
              {{totalItems}}
            </template>
            <template v-slot:transactionType>
              {{ isInExpenseSections ? $tc('transactions_table_total_count_and_amount_expense_type', totalItems): $tc('transactions_table_total_count_and_amount_transaction_type', totalItems)}}
            </template>
            <template v-slot:transactionTableTotalAmount>
              {{transactionTableTotalAmount   | currency('€', 2, {
            symbolOnLeft: false,
            thousandsSeparator: '.',
            decimalSeparator: ','
          })}}
            </template>
          </i18n>
        </div>
      </transition>
    </template>
      <transition name="actions-transition">
        <multiple-actions-bar
          v-if="type === 'transactions' && selectedTransactions.length > 0 || type === 'users' && selectedUsers.length > 0"
          :actionsList="actionsList"
          :type="type"
          @fetchUserTransactions="fetchUserTransactions"
        ></multiple-actions-bar>
      </transition>
      <table-column-selector
        v-if="columsSelector"
        :columns="columns"
        @toggleColumn="toggleColumn"
      />
    </div>
    <div
      class="Fuell-Table"
      :class="tableClass"
      ref="table"
    >
      <div
        class="Fuell-Table--header"
        :class="{'regular-user': isUser}"
        :style="tableGridTemplateStyle"
        v-if="showTableHeader"
      >
        <template v-for="column in columns">
          <div
            class="Fuell-Table--item"
            v-if="showSelect && column.key === 'select'"
            :key="column.key"
          >
            <input-checkbox
              :id="'transactions-select-all'"
              :value="selectAllFlag"
              @click="manageToggleAll"
            />
          </div>
          <div
            v-else-if="column.visible && column.key === 'options'"
            :key="column.key"
            class="Fuell-Table--item Fuell-Table--options"
          >
          </div>
          <div
            v-else-if="column.visible && column.key !== 'select'"
            :key="column.key"
            @click="sortByField(column.filter || column.key)"
            class="Fuell-Table--item"
            :class="{'Fuell-Table--sortable': column.sortable}"
          >
            <span
              v-if="hasHeader(column)"
              v-tooltip="$t(column.name)"
            >{{$t(column.name)}}</span>
            <span
              v-if="hasHeader(column)"
              class="sortable-handler"
              :class="getSortableClass(isSortedByField(column.filter || column.key), sortDirection(column.filter || column.key))"
            ></span>
          </div>
        </template>
      </div>
      <div
        v-if="showSelectTotalItems"
        class="transactions-selected--all"
      >
        <div v-if="!selectedTransactionsBulkAction">
          <i18n
            tag="span"
            class="Fuell-Text"
            path="transactions_total_transactions_bulk_selection"
          >
            <template v-slot:selectedTransactions>
              <span class="Fuell-Text"><b>{{selectedTransactions.length}}</b></span>
            </template>
            <template v-slot:transactionsTotalItems>
              <span class="Fuell-Text"><b>{{transactionsTotalItems}}</b></span>
            </template>
          </i18n>
          <i18n
            tag="span"
            class="Fuell-Text clickable"
            path="transactions_total_transactions_bulk_selection_action_select"
            @click="setBulkAction(true)"
          >
            <template v-slot:transactionsTotalItems>
              <span class="Fuell-Text"><b>{{transactionsTotalItems}}</b></span>
            </template>
          </i18n>
        </div>
        <div v-else>
          <i18n
            tag="span"
            class="Fuell-Text"
            path="transactions_total_transactions_bulk_selection_all"
          >
            <span class="Fuell-Text"><b>{{transactionsTotalItems}}</b></span>
          </i18n>
          <span
            class="Fuell-Text clickable"
            @click="setBulkAction(false)"
          >{{$t('transactions_total_transactions_bulk_selection_action_cancel_select')}}</span>
        </div>
      </div>
      <div class="Fuell-Table--content">
        <template v-if="!isLoadingTransactions">
          <component
            :is="tableRowToRender"
            v-for="transaction in rows"
            :tableName="tableName"
            :isExportTable="isExportTable"
            :columns="columns"
            :showSelect="showSelect"
            :key="transaction.id"
            :transaction="transaction"
            :is-selected="checkIfIsSelected(transaction)"
            @showReceiptModal="emitShowReceiptModal"
            @showTransactionModal="emitShowTransactionModal"
            @showMileageModal="emitShowMileageModal"
            @showDietModal="emitShowDietModal"
            @showExpenseModal="emitShowExpenseModal"
            @budgetUpdated="emitBudgetUpdated"
            @emitPendingTransactions="emitPendingTransactions"
            @emitRefundableTransactions="emitRefundableTransactions"
            @emitNotRefundableTransactions="emitNotRefundableTransactions"
          ></component>
        </template>
        <div
          class="Fuell-Table--spinner"
          v-if="isLoadingTransactions || isLoadingNextPageTransactions"
        >
          <spinner></spinner>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { segmentTrack } from '@/analytics/segment'
import Dropdown from '@/components/Commons/Dropdown'
import InputCheckbox from '@/components/Commons/Forms/InputCheckbox'
import TransactionsTableRow from './TransactionsTableRow'
import TransactionsSmallTableRow from './TransactionsSmallTableRow.vue'
import TableColumnSelector from '@/components/Commons/TableColumnSelector'
import Spinner from '@/components/Commons/Spinner'
import _transactionsTableHandler from '../../_mixins/_transactionsTableHandler'
import _transactionsTableSortingHandler from '../../_mixins/_transactionsTableSortingHandler'
import _sortableTableMixin from '@/components/Commons/_mixins/_sortableTableMixin'
import _paginationHandlerMixin from '@/components/Commons/_mixins/_paginationHandlerMixin'
const MultipleActionsBar = () => import('@/components/Commons/MultipleActionsBar')

export default {
  name: 'TransactionsTable',
  components: {
    InputCheckbox,
    Spinner,
    Dropdown,
    MultipleActionsBar,
    TableColumnSelector
  },
  props: {
    type: {
      type: String,
      default: 'transactions'
    },
    columsSelector: {
      type: Boolean,
      default: false
    },
    actionsList: {
      type: Array,
      default: () => []
    },
    tableName: {
      type: String,
      default: ''
    },
    columns: {
      type: Array,
      required: true
    },
    showSelect: {
      type: Boolean,
      default: false
    },
    isExportTable: {
      type: Boolean,
      default: true
    },
    hideColumnSelector: {
      type: Boolean,
      default: false
    }

  },
  computed: {
    transactionDashboard () {
      return this.$store.getters['TransactionsStore/transactionDashboard'] || false
    },
    totalItems () {
      return this.transactionDashboard && this.transactionDashboard.totalCount ? this.transactionDashboard.totalCount : 0
    },
    isInExpenseSections () {
      return this.$route.name.includes('reimbursements')
    },
    isInBudgetSection () {
      return this.$route.name.includes('budgets')
    },
    transactionTableTotalAmount () {
      return this.transactionDashboard && this.transactionDashboard.totalAmount ? this.transactionDashboard.totalAmount : 0
    },
    currentTransactionsAmount () {
      return this.$store.getters['TransactionsStore/loadedTransactionsAmount']
    },
    transactionsTotalItems () {
      return this.$store.getters['TransactionsStore/totalItems']
    },
    selectAllTransactionsFlag () {
      return this.$store.getters['TransactionsStore/selectAllFlag']
    },
    showSelectTotalItems () {
      return this.selectAllTransactionsFlag && (this.currentTransactionsAmount < this.transactionsTotalItems)
    },
    selectedTransactionsBulkAction () {
      return this.$store.getters['TransactionsStore/selectedTransactionsBulkAction']
    },
    isLoadingTransactions () {
      // return true
      return this.$store.getters['TransactionsStore/isLoadingTransactions']
    },
    isLoadingNextPageTransactions () {
      return this.$store.getters['TransactionsStore/isLoadingNextPageTransactions']
    },
    isBookKeeper () {
      return this.$store.getters['LoginStore/isBookKeeper']
    },
    isAdmin () {
      return this.$store.getters['LoginStore/isAdmin']
    },
    userIsOwnerOfBudget () {
      return !!(this.transaction.budget && this.transaction.budget.imOwner)
    },
    canReviewTransaction () {
      if (this.isUser && this.userIsOwnerOfBudget) {
        return true
      }
      if (this.isAdmin || this.isBookKeeper) {
        return true
      }

      return false
    },
    isUser () {
      return this.$store.getters['LoginStore/isUser']
    },
    tableGridTemplateStyle () {
      let columns = this.columns.filter(c => c.visible).map(c => c.template)

      if (!this.showSelect) {
        columns.shift()
      }

      return {
        gridTemplateColumns: columns.join(' ')
      }
    },
    selectedTransactions () {
      return this.$store.getters['TransactionsStore/selectedTransactions']
    },
    selectedUsers () {
      return this.$store.getters['TransactionsStore/selectedUsers']
    },
    selectAllFlag () {
      return this.$store.getters['TransactionsStore/selectAllFlag']
    },
    tableRowToRender () {
      if (this.$route.meta.users) {
        return TransactionsTableRow
      }
      return window.matchMedia('(max-width:576px)').matches ? TransactionsSmallTableRow : TransactionsTableRow
    },
    showTableHeader () {
      if (this.$route.meta.users) {
        return true
      }
      return !window.matchMedia('(max-width:576px)').matches
    },
    visibleColumns () {
      return this.columns.filter(el => el.visible && el.key !== 'options')
    },
    tableClass () {
      if (this.budgetSection) {
        return 'Fuell-Table--transactions-budgets'
      }
      if (this.exportSection) {
        return 'Fuell-Table--transactions-export'
      }
      return 'Fuell-Table--transactions'
    },
    exportSection () {
      return this.$route.name?.includes('export')
    },
    budgetSection () {
      return this.$route?.name.includes('budgets')
    }
  },
  mixins: [
    _transactionsTableHandler,
    _paginationHandlerMixin,
    _transactionsTableSortingHandler,
    _sortableTableMixin
  ],
  methods: {
    fetchUserTransactions (value) {
      this.$emit('fetchUserTransactions', value)
    },
    toggleColumn (column) {
      this.$store.dispatch('TransactionsStore/toggleTableColumnVisibility', column)
      segmentTrack('Transaction columns selected', {
        active_columns: this.visibleColumns.map(el => el.name).join(',')
      })
    },
    isColumnModifiable (column) {
      if (column.key === 'user' && this.isUser) {
        return false
      }
      return column.modifiable
    },
    setBulkAction (value) {
      this.$store.dispatch('TransactionsStore/setSelectedTransactionsBulkAction', value)
    },
    hasHeader (column) {
      return !column.noHeader
    },
    emitShowTransactionModal (event, modal) {
      this.$emit('showDetailTransactionModal', event, modal)
    },
    emitShowReceiptModal (event) {
      this.$emit('showReceiptModal', event)
    },
    emitBudgetUpdated (event) {
      this.$emit('budgetUpdated', event)
    },
    emitShowMileageModal (event) {
      this.$emit('showMileageModal', event)
    },
    emitShowDietModal (event) {
      this.$emit('showDietModal', event)
    },
    emitShowExpenseModal (event) {
      this.$emit('showExpenseModal', event)
    },
    checkIfIsSelected (item) {
      if (this.$route.meta.users) {
        return this.userIsSelected(item)
      } else {
        return this.transactionIsSelected(item)
      }
    },
    transactionIsSelected (transaction) {
      const isTransactionSelected = this.selectedTransactions.find(el => el.id === transaction.id)
      return !!isTransactionSelected
    },
    userIsSelected (user) {
      const isUserSelected = this.selectedUsers.find(el => el.user_id === user.user_id)
      return !!isUserSelected
    },
    toggleAllUsers () {
      if (!this.selectAllFlag) {
        this.$store.dispatch('TransactionsStore/toggleSelectAllUsers', true)
      } else {
        this.$store.dispatch('TransactionsStore/toggleSelectAllUsers', false)
      }
    },
    toggleAllTransactions () {
      if (!this.selectAllFlag) {
        this.$store.dispatch('TransactionsStore/toggleSelectAll', { selectAll: true, isExportTable: this.isExportTable })
      } else {
        this.$store.dispatch('TransactionsStore/toggleSelectAll', { selectAll: false, isExportTable: this.isExportTable })
      }
      this.$emit('allTransactionsSelected')
    },
    manageToggleAll () {
      if (this.$route.meta.users) {
        this.toggleAllUsers()
      } else {
        this.toggleAllTransactions()
      }
    },
    handlerSortCardColumn () {
      if (this.tableName === 'reimbursements') {
        this.sortByField('expense.paymentMethod.officePaymentMethod.name')
        return
      }
      this.sortByField('card.partialPAN')
    },
    getSortableClassForCardColumn () {
      if (this.tableName === 'reimbursements') {
        return this.getSortableClass(this.isSortedByField('expense.paymentMethod.officePaymentMethod.name', this.sortDirection('expense.paymentMethod.officePaymentMethod.name')))
      }
      return this.getSortableClass(this.isSortedByField('card.partialPAN', this.sortDirection('card.partialPAN')))
    },
    emitPendingTransactions (event) {
      this.$emit('goToPendingTransactions', event)
    },
    emitRefundableTransactions (event) {
      this.$emit('goToRefundableTransactions', event)
    },
    emitNotRefundableTransactions (event) {
      this.$emit('goToNotRefundableTransactions', event)
    }

  }
}
</script>
<style lang="scss" scoped>
.Fuell-Table {
  &--header {
    z-index: 11;
    width: 100%;
  }
  &--row {
    width: 100%;
  }
  // padding-bottom: 100px;
  @media screen and (max-width: 576px) {
    overflow-x: auto;
  }
  &--counter {
    display: flex;
    align-items: center;
    font-weight: 400;
    height: 32px;
    color: var(--text-body);
  }
}

.transactions-selected--all {
  position: sticky;
  top: 44px;
  margin-bottom: 5px;
  margin-top: -6px;
  padding: 10px;
  width: 100%;
  background: var(--semantic-brand-tint);
  border-radius: 8px;
  text-align: center;
  .Fuell-Text {
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: var(--text-heading);
    b {
      font-weight: 600;
    }

    &.clickable {
      color: var(--text-link);
      b {
        color: var(--text-link);
      }
    }
  }
}
.container-transition {
  position: relative;
  height: 32px;
  display: flex;
  justify-content: space-between;
  .columns-dropdown {
    margin-left: auto;
  }
}
</style>
<style lang="scss">
/* appearing */
.actions-transition-enter-active {
  transition: opacity 300ms ease-out;
  .actions-container {
    transform: translate(-90px, 0);
    transition: transform 200ms ease-out;
  }
}
.text-transition-enter-active {
  transition: opacity 300ms ease-out;
}

/* disappearing */
.actions-transition-leave-active {
  transition: opacity 300ms ease-out;
  opacity: 0;
}
.counter-transition-leave-active {
  transition: opacity 300ms ease-out;
  opacity: 0;
}

.actions-transition-enter,
.actions-transition-leave-to {
  opacity: 0;
}
.actions-transition-leave-to {
  position: absolute;
  top: 0;
  .actions-container {
    transform: translate(-90px);
    transition: transform 200ms ease-out;
  }
}
.counter-transition-enter,
.counter-transition-leave-to {
  opacity: 0;
}
.counter-transition-leave-to {
  position: absolute;
}

.actions-transition-enter-to {
  position: absolute;
  .actions-container {
    transform: translate(0, 0);
  }
}
.counter-transition-enter-to {
  position: absolute;
}
.columns-dropdown .Fuell-Dropdown--container {
  position: absolute;
  right: 4px;
  top: 40px;
  width: 256px;
  font-weight: 300;
}

.columns-dropdown .Fuell-Dropdown--container .Fuell-Dropdown--content {
  padding: 15px 20px 12px;
  color: var(--gray);
  font-size: 15px;
  width: 200px;
}

.columns-dropdown .list-item {
  line-height: 36px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.Fuell-ColumnButton {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 8px 0 8px;
  width: 52px;
  height: 25px;
  background: var(--grey-action-3-active);
  border-radius: 16px;
  border: none;
}
</style>
